<template>
  <section class="comm-page dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="teaching" @tree-node-click="treeNodeClick"></DepartmentPane>
    </section>
    <section class="dp-right">
      <CommFilterBar :buttonList="buttonList" :searchList="searchList" @callFunction="callFunction" />
      <div class="comm-panel">
        <div class="tbl-scroll-host">
          <el-table
            ref="userBindTable"
            class="comm-table"
            empty-text="暂无数据"
            :row-key="userBindTableRowKey"
            :data="rows"
            v-loading="loading"
            size="small"
            height="100%"
            style="width: 100%"
            @selection-change="handleSelectionChange"
            @row-click="onUserBindTableRowClick">
            <el-table-column type="selection" width="44" />
            <el-table-column prop="Id" label="绑定ID" width="120" show-overflow-tooltip />
            <el-table-column prop="RuleName" label="规则名称" min-width="120" show-overflow-tooltip />
            <el-table-column prop="StudentName" label="学生姓名" width="100" show-overflow-tooltip />
            <el-table-column prop="StudentNo" label="学号" width="110" show-overflow-tooltip />
            <el-table-column label="有效期" min-width="168">
              <template slot-scope="scope">
                <div class="comm-cell-stack">
                  <div><span class="comm-cell-muted">起</span>{{ scope.row.StartTime || scope.row.startTime || '—' }}</div>
                  <div :class="{ 'expired-text': isExpired(scope.row) }">
                    <span class="comm-cell-muted">止</span>{{ scope.row.ExpireTime || scope.row.expireTime || '—' }}
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="套餐状态" align="center" width="100">
              <template slot-scope="scope">
                <el-tag size="mini" :type="getPackageStatusTagType(scope.row)">
                  {{ getPackageStatus(scope.row) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="Source" label="来源" width="90" show-overflow-tooltip />
            <el-table-column label="操作" width="200" align="center">
              <template slot-scope="scope">
                <div class="table-op-pair">
                  <el-button type="text" size="small" class="table-op-link" @click="openQuotaDetail(scope.row)">配额明细</el-button>
                  <el-button v-if="isUnboundBinding(scope.row)" type="text" size="small" class="table-op-link restore-bind-btn"
                    @click="restoreBinding(scope.row)">恢复绑定</el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="comm-pager">
          <el-pagination
            layout="total, prev, pager, next, jumper"
            :current-page="page"
            :page-size="pageSize"
            :total="total"
            @current-change="handleCurrentChange" />
        </div>
      </div>

      <el-dialog title="选择人员绑定" :visible.sync="bindDialogVisible" width="920px" :close-on-click-modal="false">
        <el-alert type="info" :closable="false" show-icon class="bind-scope-alert">
          人员范围以左侧部门树当前选中节点为准：{{ departmentLabel || '（未选择，请先在左侧树选择机构/班级）' }}
        </el-alert>
        <el-form label-width="90px" style="margin-top: 12px">
          <el-form-item label="选择人员" required>
            <div class="ps-pick-summary">
              <span v-if="personSelectedItems.length">已选 <strong>{{ personSelectedItems.length }}</strong> 人</span>
              <span v-else class="muted-text">请从家校通讯录选择学生</span>
              <el-button type="primary" size="small" plain :disabled="!departmentId" @click="personPickerVisible = true">选择学生</el-button>
              <el-button v-if="personSelectedItems.length" type="text" size="small" @click="clearPersonSelection">清空</el-button>
            </div>
            <div v-if="personSelectedItems.length" class="ps-pick-tags">
              <el-tag
                v-for="item in personSelectedPreview"
                :key="item.id"
                size="mini"
                closable
                disable-transitions
                @close="removePersonSelected(item.id)">
                {{ item.name }}
              </el-tag>
              <span v-if="personSelectedItems.length > 8" class="ps-pick-more">等 {{ personSelectedItems.length }} 人</span>
            </div>
          </el-form-item>
        </el-form>
        <div slot="footer">
          <el-button @click="bindDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitBind">提交</el-button>
        </div>
      </el-dialog>

      <person-scope-multi-picker-dialog
        :visible.sync="personPickerVisible"
        directory-scope="teaching"
        :show-scope-tabs="false"
        :scope-department-id="departmentId"
        title="选择学生"
        :value="personSelectedItems"
        @confirm="onPersonsPicked"
      />

      <el-dialog :title="`月度配额明细 — ${quotaDialog.studentName || ''}`" :visible.sync="quotaDialog.visible" width="820px"
        custom-class="quota-detail-dialog" :close-on-click-modal="false">
        <el-alert class="quota-dialog-alert" type="info" :closable="false" show-icon
          title="按月份展示分配、已用与剩余分钟；点击「话单」查看该月配额扣减对应的通话记录。" />
        <el-table :data="quotaDialog.rows" v-loading="quotaDialog.loading" class="quota-month-table" size="small" stripe
          border fit :max-height="quotaMonthTableMaxHeight">
          <el-table-column label="月份" min-width="100" align="center">
            <template slot-scope="scope">{{ formatYearMonth(scope.row.YearMonth || scope.row.yearMonth) }}</template>
          </el-table-column>
          <el-table-column prop="AllocatedMinutes" label="分配(分)" min-width="88" align="center" />
          <el-table-column prop="UsedMinutes" label="已用(分)" min-width="88" align="center" />
          <el-table-column prop="RemainMinutes" label="剩余(分)" min-width="88" align="center" />
          <el-table-column label="到期时间" min-width="132" align="center" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.ExpireTime || scope.row.expireTime || '—' }}</template>
          </el-table-column>
          <el-table-column label="状态" min-width="100" align="center">
            <template slot-scope="scope">{{ formatQuotaRowStatus(scope.row) }}</template>
          </el-table-column>
          <el-table-column label="话单" min-width="88" align="center">
            <template slot-scope="scope">
              <el-button type="text" size="small" class="quota-call-link"
                @click="openCallDetailForQuota(scope.row)">话单</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="quota-dialog-pager">
          <el-pagination layout="total, prev, pager, next, jumper" :current-page="quotaDialog.page"
            :page-size="quotaDialog.pageSize" :total="quotaDialog.total" @current-change="onQuotaDialogPageChange" />
        </div>
      </el-dialog>

      <el-dialog :title="`通话明细（${formatYearMonth(callDetailDialog.yearMonth)}）— ${callDetailDialog.studentName || ''}`"
        :visible.sync="callDetailDialog.visible" width="860px" append-to-body custom-class="call-detail-dialog"
        :close-on-click-modal="false">
        <el-alert class="quota-dialog-alert" type="info" :closable="false" show-icon
          title="仅列出已关联配额 Id 的话单；无扣减或未写入配额的记录不会出现在此列表。" />
        <el-table :data="callDetailDialog.rows" v-loading="callDetailDialog.loading" class="call-detail-table"
          size="small" stripe border fit :max-height="callDetailTableMaxHeight">
          <el-table-column label="开始时间" min-width="158" show-overflow-tooltip align="center">
            <template slot-scope="scope">{{ formatCallStartTime(scope.row.startTime) }}</template>
          </el-table-column>
          <el-table-column prop="callee" label="被叫" min-width="120" show-overflow-tooltip align="center" />
          <el-table-column prop="caller" label="主叫" min-width="120" show-overflow-tooltip align="center" />
          <el-table-column label="时长" min-width="88" align="center">
            <template slot-scope="scope">{{ scope.row.durationText }}</template>
          </el-table-column>
          <el-table-column label="扣减" min-width="100" align="center">
            <template slot-scope="scope">{{ scope.row.deductText != null ? scope.row.deductText : scope.row.deductMinutes }}</template>
          </el-table-column>
          <el-table-column prop="deviceSn" label="设备SN" min-width="140" show-overflow-tooltip />
        </el-table>
        <div class="quota-dialog-pager">
          <el-pagination layout="total, prev, pager, next, jumper" :current-page="callDetailDialog.page"
            :page-size="callDetailDialog.pageSize" :total="callDetailDialog.total"
            @current-change="onCallDetailPageChange" />
        </div>
      </el-dialog>
    </section>
  </section>
</template>

<script>
import util from '../../../util/date'
import './styles/comm-page.css'
import CommFilterBar from './components/CommFilterBar.vue'
import DepartmentPane from '../../components/DepartmentPane'
import { normalizeDeptId } from '../../utils/deptCascaderProps'
import PersonScopeMultiPickerDialog from '../../components/PersonScopeMultiPickerDialog'
import { getCommRuleOptions, batchRestoreCommRuleBinding, exportCommRuleUsers, getCommBindingQuotaPage } from '../../api/modules/misc';
import { getCommRuleUserListPage, batchBindCommRuleUsers, batchUnbindCommRuleUsers } from '../../api/modules/system';
import { getCommCallRecordListPage } from '../../api/modules/communication';const defaultButtons = [
  { name: '查询', Func: 'handleQuery' },
  { name: '导出', Func: 'handleExport' },
  { name: '选择人员绑定', Func: 'openBindDialog' },
  { name: '批量解绑', Func: 'batchUnbind', btnType: 'danger' }
]

export default {
  components: { CommFilterBar, DepartmentPane, PersonScopeMultiPickerDialog },
  data() {
    return {
      loading: false,
      rows: [],
      selectionRows: [],
      page: 1,
      pageSize: 10,
      total: 0,
      departmentId: '',
      departmentLabel: '',
      filters: {},
      searchList: [
        { label: '套餐', key: 'ruleId', type: 'select', options: [{ label: '全部', value: '' }], width: '180px' },
        { label: '姓名', key: 'studentName', width: '160px', placeholder: '姓名' }
      ],
      ruleOptions: [{ label: '全部', value: '' }],
      buttonList: [],
      bindDialogVisible: false,
      bindRuleId: 0,
      personSelectedItems: [],
      personPickerVisible: false,
      quotaDialog: {
        visible: false,
        studentName: '',
        bindingId: null,
        rows: [],
        loading: false,
        page: 1,
        pageSize: 8,
        total: 0
      },
      callDetailDialog: {
        visible: false,
        loading: false,
        rows: [],
        total: 0,
        page: 1,
        pageSize: 15,
        quotaId: null,
        studentId: null,
        yearMonth: '',
        studentName: ''
      }
    }
  },
  computed: {
    personSelectedPreview() {
      return (this.personSelectedItems || []).slice(0, 8)
    },
    /** 行数少时不锁 max-height，避免表体区域大块空白；行数多时再限制高度并出现纵向滚动。最后修改：2026-05-12 */
    quotaMonthTableMaxHeight() {
      const rows = (this.quotaDialog && this.quotaDialog.rows) || []
      return rows.length > 8 ? 440 : undefined
    },
    callDetailTableMaxHeight() {
      const rows = (this.callDetailDialog && this.callDetailDialog.rows) || []
      return rows.length > 8 ? 380 : undefined
    }
  },
  methods: {
    parseDateTime(value) {
      if (!value) return null
      if (value instanceof Date) return value
      const dt = new Date(String(value).replace(/-/g, '/'))
      return Number.isNaN(dt.getTime()) ? null : dt
    },
    isExpired(row) {
      const expireTime = this.parseDateTime(row && row.ExpireTime)
      if (!expireTime) return false
      return expireTime.getTime() < Date.now()
    },
    getPackageStatus(row) {
      if (row && row.RuleEnabled === false) return '规则已禁用'
      const st = Number(row && row.Status)
      if (st === 3) return '已解绑'
      if (st === 2) return '已失效'
      if (st === 0) return '未生效'
      if (st !== 1) return '未知'
      if (this.isExpired(row)) return '已过期'
      return '生效中'
    },
    getPackageStatusTagType(row) {
      const status = this.getPackageStatus(row)
      if (status === '生效中') return 'success'
      if (status === '规则已禁用') return 'warning'
      if (status === '已解绑') return 'warning'
      if (status === '未生效' || status === '已失效') return 'info'
      return 'danger'
    },
    /** 绑定状态 3=已解绑（可恢复） */
    isUnboundBinding(row) {
      return Number(row && row.Status) === 3
    },
    /**
     * 将配额列表行统一为 PascalCase，避免 el-table-column prop 与接口 camelCase 不一致导致单元格空白。最后修改：2026-05-12
     */
    normalizeQuotaListRow(raw) {
      if (!raw || typeof raw !== 'object') return {}
      const n = (v, alt) => (v !== undefined && v !== null ? v : alt)
      const num = (v, alt) => {
        const x = n(v, alt)
        const t = Number(x)
        return Number.isFinite(t) ? t : 0
      }
      return {
        ...raw,
        Id: n(raw.Id, raw.id),
        BindingId: n(raw.BindingId, raw.bindingId),
        RuleId: n(raw.RuleId, raw.ruleId),
        StudentId: n(raw.StudentId, raw.studentId),
        YearMonth: n(raw.YearMonth, raw.yearMonth),
        ExpireTime: n(raw.ExpireTime, raw.expireTime),
        Status: raw.Status !== undefined && raw.Status !== null ? raw.Status : raw.status,
        AllocatedMinutes: num(raw.AllocatedMinutes, raw.allocatedMinutes),
        UsedMinutes: num(raw.UsedMinutes, raw.usedMinutes),
        RemainMinutes: num(raw.RemainMinutes, raw.remainMinutes)
      }
    },
    /**
     * 配额行展示状态：已过期除 Status=3 外，若 Status=1 且 ExpireTime 已过也显示已过期。最后修改：2026-05-12
     */
    formatQuotaRowStatus(row) {
      if (!row || typeof row !== 'object') return '—'
      const s = Number(row.Status != null ? row.Status : row.status)
      if (Number.isNaN(s)) return '—'
      if (s === 0) return '未生效'
      if (s === 4) return '随绑定解绑'
      if (s === 2) return '已用尽'
      if (s === 3) return '已过期'
      const exp = this.parseDateTime(row.ExpireTime != null ? row.ExpireTime : row.expireTime)
      if (s === 1 && exp && exp.getTime() < Date.now()) return '已过期'
      if (s === 1) return '生效中'
      return '已失效'
    },
    /** 将 202605 格式化为 2026-05 便于阅读 */
    formatYearMonth(val) {
      const s = String(val == null ? '' : val).trim()
      if (s.length === 6 && /^\d{6}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}`
      return s || '—'
    },
    onQuotaDialogPageChange(p) {
      this.quotaDialog.page = p
      this.loadQuotaDialogPage()
    },
    /** 话单行：兼容接口 PascalCase；应扣/实扣拆分展示。最后修改：2026-08-11 */
    normalizeCallRecordRow(raw) {
      if (!raw || typeof raw !== 'object') return {}
      const st = raw.startTime != null ? raw.startTime : raw.StartTime
      const sec = Number(raw.durationSeconds != null ? raw.durationSeconds : raw.DurationSeconds) || 0
      const requested = Number(raw.requestedDeductMinutes != null ? raw.requestedDeductMinutes : raw.RequestedDeductMinutes) || 0
      const dm = Number(raw.deductMinutes != null ? raw.deductMinutes : raw.DeductMinutes) || 0
      const recordSource = raw.recordSource != null ? raw.recordSource : raw.RecordSource
      const isTbVideophone = recordSource === 'TbVideophone'
      let deductText = '天波免费'
      if (!isTbVideophone) {
        if (requested > 0 && requested !== dm) {
          deductText = '应扣 ' + requested + ' / 实扣 ' + dm + ' 分钟'
        } else {
          deductText = '扣减 ' + dm + ' 分钟'
        }
      }
      return {
        startTime: st,
        callee: raw.callee != null ? raw.callee : raw.Callee,
        caller: raw.caller != null ? raw.caller : raw.Caller,
        deviceSn: raw.deviceSn != null ? raw.deviceSn : raw.DeviceSn,
        durationText: `${Math.floor(sec / 60)}分${sec % 60}秒`,
        requestedDeductMinutes: requested,
        deductMinutes: dm,
        deductText
      }
    },
    formatCallStartTime(val) {
      if (!val || val === '') return ''
      return util.formatDate.format(new Date(val), 'yyyy-MM-dd hh:mm:ss')
    },
    onCallDetailPageChange(p) {
      this.callDetailDialog.page = p
      this.loadCallDetailPage()
    },
    openCallDetailForQuota(quotaRow) {
      const qid = quotaRow.Id != null ? quotaRow.Id : quotaRow.id
      const sid = quotaRow.StudentId != null ? quotaRow.StudentId : quotaRow.studentId
      const ym = quotaRow.YearMonth != null ? quotaRow.YearMonth : quotaRow.yearMonth
      if (!qid) {
        this.$message.warning('无法识别配额记录')
        return
      }
      this.callDetailDialog = {
        ...this.callDetailDialog,
        visible: true,
        loading: false,
        rows: [],
        total: 0,
        page: 1,
        pageSize: 15,
        quotaId: qid,
        studentId: sid,
        yearMonth: ym || '',
        studentName: this.quotaDialog.studentName || ''
      }
      this.loadCallDetailPage()
    },
    async loadCallDetailPage() {
      const qid = this.callDetailDialog.quotaId
      if (!qid) return
      this.callDetailDialog.loading = true
      try {
        const params = {
          page: this.callDetailDialog.page,
          pageSize: this.callDetailDialog.pageSize,
          quotaId: qid,
          departmentId: ''
        }
        const sid = Number(this.callDetailDialog.studentId)
        if (sid > 0) params.studentId = sid
        const res = await getCommCallRecordListPage(params)
        if (res.data.success) {
          const pageData = res.data.response || res.data.Response || {}
          const list = pageData.data || pageData.Data || []
          this.callDetailDialog.rows = list.map((x) => this.normalizeCallRecordRow(x))
          this.callDetailDialog.total =
            pageData.dataCount != null ? pageData.dataCount : (pageData.DataCount || 0)
        } else {
          this.$message.error(res.data.msg || '加载通话明细失败')
        }
      } finally {
        this.callDetailDialog.loading = false
      }
    },
    callFunction(item) {
      this.filters = typeof item.search === 'object' ? item.search : {}
      if (typeof this[item.Func] === 'function') this[item.Func]()
    },
    treeNodeClick(data) {
      this.departmentId = data && data.value ? data.value : ''
      this.departmentLabel = data && data.label ? data.label : ''
      this.loadRuleOptions()
      this.page = 1
      this.handleQuery()
    },
    async loadRuleOptions() {
      if (!this.departmentId) {
        this.ruleOptions = [{ label: '全部', value: '' }]
        this.searchList[0].options = this.ruleOptions
        this.filters.ruleId = ''
        return
      }
      try {
        const res = await getCommRuleOptions({ departmentId: this.departmentId })
        if (res.data.success) {
          const rows = res.data.response || []
          this.ruleOptions = [{ label: '全部', value: '' }].concat(rows.map(x => ({ label: x.RuleName, value: x.Id })))
          this.searchList[0].options = this.ruleOptions
          const hasCurrent = this.ruleOptions.some(x => String(x.value) === String(this.filters.ruleId || ''))
          if (!hasCurrent) this.filters.ruleId = ''
        } else {
          this.ruleOptions = [{ label: '全部', value: '' }]
          this.searchList[0].options = this.ruleOptions
        }
      } catch (e) {
        this.ruleOptions = [{ label: '全部', value: '' }]
        this.searchList[0].options = this.ruleOptions
      }
    },
    handleSelectionChange(rows) {
      this.selectionRows = rows || []
    },
    /** 表格 row-key，兼容 Id / id */
    userBindTableRowKey(row) {
      if (!row) return ''
      const id = row.Id != null ? row.Id : row.id
      return id != null ? id : ''
    },
    /**
     * 点击行切换勾选（勾选列、操作列内按钮及表单控件不触发）。最后修改：2026-05-12
     */
    onUserBindTableRowClick(row, column, event) {
      const table = this.$refs.userBindTable
      if (!table || !row) return
      if (column && column.type === 'selection') return
      const el = event && event.target
      if (el && el.closest && el.closest('.el-button, button, a, input, textarea, select, label, .el-switch, .el-radio, .el-checkbox, .el-input, .el-link')) {
        return
      }
      table.toggleRowSelection(row)
    },
    handleCurrentChange(page) {
      this.page = page
      this.handleQuery()
    },
    async handleQuery() {
      this.loading = true
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize,
          ruleId: this.filters.ruleId || this.$route.query.ruleId || '',
          studentName: this.filters.studentName || '',
          departmentId: this.departmentId || ''
        }
        const res = await getCommRuleUserListPage(params)
        if (res.data.success) {
          const pageData = res.data.response || {}
          this.rows = pageData.data || []
          this.total = pageData.dataCount || 0
        } else {
          this.$message.error(res.data.msg || '查询失败')
        }
      } finally {
        this.loading = false
      }
    },
    async openBindDialog() {
      const ruleId = Number(this.filters.ruleId || this.$route.query.ruleId || 0)
      if (!ruleId) {
        this.$message.warning('请先在查询条件中指定规则ID')
        return
      }
      const departmentId = normalizeDeptId(this.departmentId)
      if (!departmentId) {
        this.$message.warning('请先在左侧部门树选择机构或班级')
        return
      }
      this.bindRuleId = ruleId
      this.clearPersonSelection()
      this.bindDialogVisible = true
    },
    onPersonsPicked(people) {
      this.personSelectedItems = (people || []).map((p) => ({
        id: String(p.id),
        name: p.name || String(p.id),
        personType: 'student',
        deptId: p.deptId != null ? String(p.deptId) : '0',
        deptLabel: p.deptLabel || ''
      }))
    },
    clearPersonSelection() {
      this.personSelectedItems = []
    },
    removePersonSelected(id) {
      const key = String(id)
      this.personSelectedItems = (this.personSelectedItems || []).filter((x) => String(x.id) !== key)
    },
    async submitBind() {
      const studentIds = (this.personSelectedItems || [])
        .map((x) => Number(x.id))
        .filter((id) => id > 0)
      if (!this.bindRuleId || !studentIds.length) {
        this.$message.warning('请先选择人员')
        return
      }
      const res = await batchBindCommRuleUsers({ RuleId: this.bindRuleId, StudentIds: studentIds, Source: 'admin' })
      if (res.data.success) {
        this.$message.success(res.data.msg || '绑定成功')
        this.bindDialogVisible = false
        this.clearPersonSelection()
        this.handleQuery()
      } else {
        this.$message.error(res.data.msg || '绑定失败')
      }
    },
    async batchUnbind() {
      if (!this.selectionRows.length) {
        this.$message.warning('请选择要解绑的数据')
        return
      }
      const preview = this.selectionRows
        .map((x) => x.StudentName || x.studentName || `ID:${x.Id}`)
        .join('、')
      try {
        await this.$confirm(
          `即将解绑选中的 ${this.selectionRows.length} 条记录：${preview}。解绑后不可用该套餐拨打电话；可在列表中「恢复绑定」重新生效（需无同规则其他有效绑定且规则未停用）。`,
          '解绑确认',
          { confirmButtonText: '确定解绑', cancelButtonText: '取消', type: 'warning' }
        )
      } catch {
        return
      }
      const ids = this.selectionRows.map((x) => x.Id)
      const res = await batchUnbindCommRuleUsers({ BindingIds: ids })
      if (res.data.success) {
        this.$message.success(res.data.msg || '解绑成功')
        this.handleQuery()
      } else {
        this.$message.error(res.data.msg || '解绑失败')
      }
    },
    async restoreBinding(row) {
      const id = row.Id != null ? row.Id : row.id
      const name = row.StudentName || row.studentName || ''
      if (!this.isUnboundBinding(row)) {
        this.$message.warning('仅已解绑记录可恢复')
        return
      }
      try {
        await this.$confirm(
          `确定恢复学生「${name || id}」的这条套餐绑定吗？沿用原到期时间与配额，不重新发放。`,
          '恢复绑定确认',
          { confirmButtonText: '确定恢复', cancelButtonText: '取消', type: 'warning' }
        )
      } catch {
        return
      }
      const res = await batchRestoreCommRuleBinding({ BindingIds: [id] })
      if (res.data.success) {
        this.$message.success(res.data.msg || '恢复成功')
        this.handleQuery()
      } else {
        this.$message.error(res.data.msg || '恢复失败')
      }
    },
    async openQuotaDetail(row) {
      const bindingId = row.Id != null ? row.Id : row.id
      this.quotaDialog = {
        ...this.quotaDialog,
        visible: true,
        studentName: row.StudentName || row.studentName || '',
        bindingId,
        rows: [],
        total: 0,
        page: 1,
        pageSize: 8,
        loading: true
      }
      await this.loadQuotaDialogPage()
    },
    /** 分页拉取月度配额（按绑定 Id，每页 8 条）。最后修改：2026-06-25 */
    async loadQuotaDialogPage() {
      const q = this.quotaDialog
      const bindingId = Number(q.bindingId)
      if (!bindingId) {
        q.loading = false
        return
      }
      q.loading = true
      try {
        const res = await getCommBindingQuotaPage({
          bindingId,
          page: q.page,
          pageSize: q.pageSize
        })
        if (res.data.success) {
          const pageData = res.data.response || res.data.Response || {}
          const rawList = pageData.data || pageData.Data || []
          q.rows = rawList.map((x) => this.normalizeQuotaListRow(x))
          q.total = pageData.dataCount != null ? pageData.dataCount : (pageData.DataCount || 0)
        } else {
          this.$message.error(res.data.msg || '获取配额明细失败')
        }
      } finally {
        q.loading = false
      }
    },
    async handleExport() {
      const params = {
        ruleId: this.filters.ruleId || this.$route.query.ruleId || '',
        studentName: this.filters.studentName || '',
        departmentId: this.departmentId || ''
      }
      const res = await exportCommRuleUsers(params)
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `话机规则绑定_${new Date().getTime()}.xlsx`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
  },
  mounted() {
    this.buttonList = defaultButtons
    this.searchList[0].options = this.ruleOptions
  }
}
</script>

<style scoped>
.ps-pick-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  color: #475569;
  font-size: 13px;
}
.ps-pick-tags {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ps-pick-more {
  color: #94a3b8;
  font-size: 12px;
  line-height: 24px;
}
.muted-text {
  color: #94a3b8;
}
.all_page_border {
  padding: 12px;
  background: #fff;
}

.expired-text {
  color: #f56c6c;
  font-weight: 500;
}
</style>
<style>
/* custom-class 挂在弹窗外层，不用 scoped */
.quota-detail-dialog .el-dialog__header,
.call-detail-dialog .el-dialog__header {
  padding: 14px 20px 10px;
  border-bottom: 1px solid #ebeef5;
}

.quota-detail-dialog .el-dialog__title,
.call-detail-dialog .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.quota-detail-dialog .el-dialog__body,
.call-detail-dialog .el-dialog__body {
  padding: 12px 20px 18px;
}

.quota-detail-dialog .quota-dialog-alert,
.call-detail-dialog .quota-dialog-alert {
  margin-bottom: 12px;
  line-height: 1.5;
}

.quota-detail-dialog .quota-month-table th,
.quota-detail-dialog .quota-month-table td,
.call-detail-dialog .call-detail-table th,
.call-detail-dialog .call-detail-table td {
  padding: 8px 10px;
}

.quota-detail-dialog .quota-month-table,
.call-detail-dialog .call-detail-table {
  width: 100%;
}

.quota-detail-dialog .quota-dialog-pager,
.call-detail-dialog .quota-dialog-pager {
  margin-top: 12px;
  text-align: right;
  line-height: 32px;
}

/* 分页不占满整行，与主列表右下角效果一致 */
.quota-detail-dialog .quota-dialog-pager .el-pagination,
.call-detail-dialog .quota-dialog-pager .el-pagination {
  display: inline-block;
  vertical-align: middle;
}

.quota-detail-dialog .quota-call-link {
  padding: 0;
}
</style>
